/* eslint-disable react-refresh/only-export-components */
import { useState, useSyncExternalStore } from "react";
import { ConfirmationModal } from "./ConfirmationModal.tsx";
import { checkForAppUpdate } from "./checkForAppUpdate.ts";

/**
 * "Check For Updates" — the confirmation behind the drawer item (in-game
 * parity, client 2026-10-07).
 *
 * It used to be a bare `window.location.reload()`, which re-runs the SAME
 * cached build. Now it asks the service worker for a new version, waits
 * (bounded) for it to install and take over, and THEN reloads — onto the new
 * build when there is one. The reload happens either way (offline, up to
 * date, a slow check), as before.
 *
 * Mounted once at the app root, like the update prompt: the drawer item only
 * asks for it and closes the drawer, so the dialog never sits under the
 * drawer or goes away with it.
 */

let open = false;
const listeners = new Set<() => void>();
const setOpen = (next: boolean) => {
  if (open === next) return;
  open = next;
  listeners.forEach((listener) => listener());
};

/** Show the "Check For Updates" confirmation. */
export const openCheckForUpdates = (): void => setOpen(true);
/** Close it (Cancel does the same). */
export const closeCheckForUpdates = (): void => setOpen(false);

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const CheckForUpdatesDialog = ({
  check = checkForAppUpdate,
  reload = () => window.location.reload(),
}: {
  /** Injected in tests. */
  check?: () => Promise<unknown>;
  reload?: () => void;
} = {}) => {
  const visible = useSyncExternalStore(
    subscribe,
    () => open,
    () => false
  );
  const [checking, setChecking] = useState(false);

  const confirm = () => {
    if (checking) return;
    setChecking(true);
    void (async () => {
      try {
        // Pull a NEW APP VERSION if one has shipped, so the reload lands on it.
        await check();
      } catch (error) {
        console.warn("App update check failed", error);
      } finally {
        reload();
      }
    })();
  };

  return (
    <ConfirmationModal
      visible={visible}
      title="Confirmation"
      onClose={closeCheckForUpdates}
      onConfirm={confirm}
      isLoading={checking}
      loadingText="Checking..."
    >
      <p className="font-medium">Would you like to check for updates now?</p>
    </ConfirmationModal>
  );
};
